// Discontinuous matching for separable resultative verbs (ลองดู → ลอง … ดู,
// กินไม่ลง → กิน … ไม่ลง). Shared between the offline example-sentence scanner
// (scripts/separable-verbs/build-split-links.ts) and the runtime translate
// grouping (src/api/thai2english-api.ts) so both surfaces agree on what counts
// as a split occurrence. Patterns come from ai/separable-verbs/seed.jsonl via
// src/data/separable-verbs.json (npm run generate:separables).

// Type-only on purpose: the translate worker bundles this module for its own
// token collection, and a value import would make esbuild resolve
// ./interfaces, whose `core/*` path alias only the root tsconfig defines.
import type { ApiWordObject } from './interfaces'

// Same shape as src/data/separable-verbs.json rows.
export interface SeparablePattern {
   w: string // canonical entry word (ลองดู)
   p1: string // first part (ลอง)
   p2: string // resultative part (ดู, or ไม่ลง for lexicalized negatives)
   // Per-pattern precision overrides (seed fields maxGap/blockBefore):
   mg?: number // tighter max gap than the global default, for risky part2s
   bb?: string[] // tokens that must not immediately precede part2 (ทาง before ออก)
}

export interface SeparableMatch {
   word: string // canonical entry word
   // Token indexes of the parts, ascending: [part1, ...part2 span]. part2 may
   // span two tokens when a negative pattern's ไม่ is its own token (ไม่ ลง).
   indexes: number[]
}

export interface SeparableMatchOptions {
   // Most gap tokens between the parts (exclusive of the parts themselves).
   maxGap?: number
   // Require part2 to end its clause: only final particles / non-Thai tokens
   // may follow it. The main false-positive guard for runtime matching; the
   // offline pipeline relaxes it because candidates are human/LLM-reviewed.
   requireClauseFinal?: boolean
}

// Particles that may legitimately trail a clause-final resultative
// (กินไม่ลงหรอก, ลองชิมดูสิ, จำหน้าไม่ได้เลย).
const FINAL_PARTICLES = new Set([
   'นะ',
   'ครับ',
   'ค่ะ',
   'คะ',
   'ไหม',
   'มั้ย',
   'เหรอ',
   'หรอ',
   'หรอก',
   'สิ',
   'เลย',
   'แล้ว',
   'หน่อย',
   'ด้วย',
   'ๆ',
])

// Connectors that RELIABLY mark a clause boundary: a gap containing one means
// part1 and part2 are in different clauses (มองหาโรงแรม แต่ หาไม่เจอ must not
// bridge แต่; โทรหาเพื่อน เพื่อ นัดเจอ must not claim เจอ). Deliberately NOT
// included: และ/หรือ, which can coordinate object NPs inside the gap
// (หากุญแจหรือกระเป๋าสตางค์ไม่เจอ).
// แล้ว mid-gap is the sequencer "then" between clauses (เลิกคิด แล้ว ลองทำดู);
// trailing แล้ว is still fine — it's separately allowed in FINAL_PARTICLES.
const GAP_CLAUSE_BREAKERS = new Set(['แต่', 'เพราะ', 'ถ้า', 'เพื่อ', 'แล้ว', 'แล้วก็'])

// ว่า opens a complement clause (คิดว่าทำได้ — the ได้ belongs to ทำ, not to
// คิด…ได้) UNLESS a naming noun precedes it, where ว่า is quotative and the
// phrase is still the verb's object NP (จำ คำ ว่า 'สวัสดี' ได้).
const QUOTATIVE_HEADS = new Set(['คำ', 'ชื่อ'])
const gapHasClauseBreak = (gapTokens: string[]): boolean =>
   gapTokens.some(
      (token, k) =>
         GAP_CLAUSE_BREAKERS.has(token) ||
         (token === 'ว่า' && !QUOTATIVE_HEADS.has(gapTokens[k - 1] ?? ''))
   )

// Mirrors normalizeThaiToken in src/api/thai2english-api.ts: tokens in spaced
// text may carry surrounding punctuation.
const stripToken = (token: string): string =>
   token.replace(/^[^฀-๿a-zA-Z0-9]+|[^฀-๿a-zA-Z0-9]+$/g, '')

const isThaiToken = (token: string): boolean => /^[฀-๿]+$/.test(token)

const matchPart2 = (
   tokens: string[],
   part1Index: number,
   pattern: SeparablePattern,
   maxGap: number,
   requireClauseFinal: boolean
): SeparableMatch | null => {
   const effectiveMaxGap = Math.min(maxGap, pattern.mg ?? maxGap)
   // j - part1Index - 1 gap tokens between the parts: at least 1 (gap 0 is the
   // contiguous compound), at most effectiveMaxGap.
   for (
      let j = part1Index + 2;
      j <= part1Index + 1 + effectiveMaxGap && j < tokens.length;
      j += 1
   ) {
      // part2 as a single token, or as two tokens (a negative's ไม่ + core).
      let span = 0
      if (tokens[j] === pattern.p2) span = 1
      else if (j + 1 < tokens.length && tokens[j] + tokens[j + 1] === pattern.p2) span = 2
      if (span === 0) continue

      const gapTokens = tokens.slice(part1Index + 1, j)
      // Punctuation, non-Thai text, or a clause-boundary connector between the
      // parts means they're most likely in different clauses.
      if (!gapTokens.every(isThaiToken) || gapHasClauseBreak(gapTokens)) return null
      // A bare ไม่ right before part2 negates it: only a pattern that owns the
      // ไม่ (p2 = ไม่ลง) may match there, never the positive pattern.
      if (gapTokens[gapTokens.length - 1] === 'ไม่' && !pattern.p2.startsWith('ไม่')) {
         return null
      }
      // Pattern-specific collocation guard: a blocked token immediately before
      // part2 means part2 belongs to a compound noun, not this verb (ทาง before
      // ออก in มอง หา ป้าย ทาง ออก — that's ทางออก "exit").
      if (
         pattern.bb &&
         gapTokens.length > 0 &&
         pattern.bb.includes(gapTokens[gapTokens.length - 1])
      ) {
         return null
      }

      if (requireClauseFinal) {
         for (let k = j + span; k < tokens.length; k += 1) {
            const trailing = tokens[k]
            // Punctuation (stripped to '') or non-Thai text ends the clause.
            if (trailing === '' || !isThaiToken(trailing)) break
            if (!FINAL_PARTICLES.has(trailing)) return null
         }
      }

      const indexes = [part1Index]
      for (let s = 0; s < span; s += 1) indexes.push(j + s)
      return { word: pattern.w, indexes }
   }
   return null
}

// Find non-overlapping split occurrences of the given patterns in a token
// list (spaced Thai text split on spaces). Contiguous occurrences (gap 0) are
// NOT matched — those are the ordinary compound, handled by normal lookup and
// the contiguous phrase pipeline. Scans left to right; where several patterns
// share a part1, the longest part2 wins (นอน … ไม่ หลับ is นอนไม่หลับ, never
// นอนหลับ with ไม่ in the gap).
export function findSeparableMatches(
   rawTokens: string[],
   patterns: SeparablePattern[],
   options: SeparableMatchOptions = {}
): SeparableMatch[] {
   const { maxGap = 4, requireClauseFinal = true } = options
   const byPart1 = new Map<string, SeparablePattern[]>()
   for (const pattern of patterns) {
      const group = byPart1.get(pattern.p1) ?? []
      group.push(pattern)
      byPart1.set(pattern.p1, group)
   }
   for (const group of byPart1.values()) {
      group.sort((a, b) => b.p2.length - a.p2.length)
   }

   const tokens = rawTokens.map(stripToken)
   const matches: SeparableMatch[] = []

   let i = 0
   while (i < tokens.length) {
      const candidates = byPart1.get(tokens[i])
      if (!candidates) {
         i += 1
         continue
      }
      let found: SeparableMatch | null = null
      for (const pattern of candidates) {
         const match = matchPart2(tokens, i, pattern, maxGap, requireClauseFinal)
         if (match) {
            found = match
            break
         }
      }
      if (found) {
         matches.push(found)
         i = found.indexes[found.indexes.length - 1] + 1
      } else {
         i += 1
      }
   }
   return matches
}

// Mark one split occurrence on the rendered word objects: all members share a
// groupId (hover highlights the whole group), show the canonical entry's
// meanings, and carry the canonical word / combined "ลอง … ดู" form / canonical
// phonetic so tooltips and the word breakdown present the group as one entry.
// All but the first member are hidden from the word breakdown. `indexes` are
// positions in `words`, ascending — part1 first, then the (adjacent) part2
// tokens.
export function groupSeparableTokens(
   words: ApiWordObject[],
   canonical: { word: string; t2e?: string; meanings: { meaning: string }[] },
   indexes: number[]
): void {
   const members = indexes.filter((i) => i >= 0 && i < words.length)
   if (members.length < 2) return
   // Index-drift guard: `indexes` refer to raw spaced-text tokens, but `words`
   // may have expanded an unknown token into sub-words. Only group when the
   // member tokens actually rebuild the canonical word.
   const rebuilt = members.map((i) => stripToken(words[i].word)).join('')
   if (rebuilt !== canonical.word) return
   const part2Display = members
      .slice(1)
      .map((i) => stripToken(words[i].word))
      .join('')
   const groupDisplay = `${stripToken(words[members[0]].word)} … ${part2Display}`
   const groupId = `${canonical.word}@${members[0]}`
   for (const [order, i] of members.entries()) {
      words[i].groupId = groupId
      words[i].groupWord = canonical.word
      words[i].groupDisplay = groupDisplay
      words[i].groupPhonetic = canonical.t2e
      words[i].meanings = canonical.meanings
      if (order > 0) words[i].hideInBreakdown = true
   }
}
